import { Link } from 'react-router-dom';
import { useSyncs, SyncRun } from '../api';

export default function SyncHistory() {
  const { data, isLoading, error } = useSyncs({ limit: 100 });

  if (isLoading) return <p className="text-slate-500 dark:text-slate-400">Loading…</p>;
  if (error) return <p className="text-red-600 dark:text-red-400">Error loading sync history.</p>;
  if (!data) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">LCR sync history</h1>
        <Link to="/" className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:underline">
          ← Dashboard
        </Link>
      </div>
      {data.syncs.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">No sync runs recorded yet.</p>
      ) : (
        <div className="overflow-auto rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/50">
              <tr className="border-b border-slate-200 dark:border-slate-700 text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                <th className="px-3 py-2">Finished</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Duration</th>
                <th className="px-3 py-2">Source file</th>
                <th className="px-3 py-2 text-right">Households Δ</th>
                <th className="px-3 py-2 text-right">People Δ</th>
                <th className="px-3 py-2 text-right">Callings Δ</th>
                <th className="px-3 py-2">Error</th>
              </tr>
            </thead>
            <tbody>
              {data.syncs.map((s) => (
                <SyncRow key={s.id} sync={s} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SyncRow({ sync: s }: { sync: SyncRun }) {
  const finished = s.finished_at ? new Date(s.finished_at) : null;
  const started = new Date(s.started_at);
  const duration = finished
    ? formatDuration(finished.getTime() - started.getTime())
    : 'running…';

  return (
    <tr className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800">
      <td className="px-3 py-2 tabular-nums text-slate-700 dark:text-slate-300">
        {finished ? finished.toLocaleString() : started.toLocaleString()}
      </td>
      <td className="px-3 py-2">
        <span className={statusClass(s.status)}>{s.status}</span>
      </td>
      <td className="px-3 py-2 text-xs tabular-nums text-slate-500 dark:text-slate-400">{duration}</td>
      <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">{s.source_file}</td>
      <td className="px-3 py-2 text-right tabular-nums">
        +{s.households_upserted}/-{s.households_removed}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        +{s.people_upserted}/-{s.people_removed}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        +{s.callings_opened}/-{s.callings_closed}
      </td>
      <td className="px-3 py-2 text-xs text-rose-700 dark:text-rose-300">{s.error_message ?? ''}</td>
    </tr>
  );
}

function statusClass(status: SyncRun['status']): string {
  const base = 'rounded px-2 py-0.5 text-xs';
  if (status === 'succeeded') return `${base} bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-300`;
  if (status === 'failed') return `${base} bg-rose-100 dark:bg-rose-900/50 text-rose-700 dark:text-rose-300`;
  return `${base} bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300`;
}

function formatDuration(ms: number): string {
  if (ms < 0) return '';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  return `${m}m ${rs}s`;
}
